import { useCallback, useEffect, useState } from "react";

import { UseFormReturn } from "react-hook-form";

import { FeatureType } from "src/clients/features-control";
import {
  autoAssignColumnTypes,
  ColumnType,
  getEntityColumnFromTypes,
  handleFeatureGenerationFromColumnTypes,
  PreviewData,
} from "src/featureCatalogue/Queries/Create/helpers";
import { QueryFormValues } from "src/featureCatalogue/Queries/Create/schema";
import { inferDataTypes } from "src/featureCatalogue/Queries/SQLResonsePreviewTable";

export const useFeatureAutoMapping = (form: UseFormReturn<QueryFormValues>) => {
  const [columnTypes, setColumnTypes] = useState<Record<string, ColumnType>>(
    {},
  );
  const [featureDatatype, setFeatureDatatype] = useState<
    Record<string, FeatureType | undefined>
  >({});

  const windowLength = form.watch("window_length");

  useEffect(() => {
    if (Object.keys(columnTypes).length > 0) {
      const entityName = form.getValues("columns.entities.0.entity_name");
      const existingFeatures = form.getValues("columns.features") || [];

      // Apply entity column
      const entityColumn = getEntityColumnFromTypes(columnTypes);
      if (entityColumn && entityName) {
        form.setValue("columns.entities.0.column_name", entityColumn);
      }

      // Apply grouping key — only one is allowed.
      const groupingKeyColumn = Object.entries(columnTypes).find(
        ([_, type]) => type === "grouping_key",
      )?.[0];
      form.setValue(
        "columns.grouping_keys",
        groupingKeyColumn ? [groupingKeyColumn] : [],
      );

      // Generate and apply features
      const features = handleFeatureGenerationFromColumnTypes(
        columnTypes,
        existingFeatures,
        windowLength,
        featureDatatype,
      );
      form.setValue("columns.features", features);
    }
  }, [columnTypes, form, featureDatatype, windowLength]);

  const setColumnType = useCallback((column: string, type: ColumnType) => {
    setColumnTypes((prev) => ({ ...prev, [column]: type }));
  }, []);

  const handleFeatureGeneration = useCallback((previewData: PreviewData) => {
    setFeatureDatatype(inferDataTypes(previewData));
    setColumnTypes((currentTypes) =>
      autoAssignColumnTypes(previewData, currentTypes),
    );
  }, []);

  // Clears internal state when change the entity so stale mappings from a previous entity/preview are not re-applied.
  const reset = useCallback(() => {
    setColumnTypes({});
    setFeatureDatatype({});
  }, []);

  return {
    columnTypes,
    setColumnType,
    handleFeatureGeneration,
    reset,
  };
};
